import { useState } from 'react'
import { api } from '@/lib/api'
import { fieldLabel, relative, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from './ui/dialog'
import { Input } from './ui/form'
import { DEFAULT_PAGE_SIZE, ListToolbar, Pagination } from './ui/listing'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState } from './ui/primitives'

/**
 * A record's history: who changed what, when, and what each field went from and to.
 *
 * Capture has been running since the first release, so this shows real history from the
 * day it ships rather than starting an empty log.
 */

export interface AuditChange {
  field: string
  before: string | null
  after: string | null
}

export interface AuditEntry {
  id: string
  entityType: string
  entityId: string
  action: 'Created' | 'Updated' | 'Deleted'
  occurredAt: string
  actorName: string
  actorEmail: string | null
  changes: AuditChange[]
}

export interface AuditTrail {
  entries: AuditEntry[]
  totalCount: number
  skip: number
  take: number
}

const TONE = {
  Created: 'positive',
  Updated: 'accent',
  Deleted: 'critical',
} as const

function Value({ value }: { value: string | null }) {
  if (value === null || value === '') {
    return <span className="text-faint italic">empty</span>
  }

  return <span className="break-words">{value}</span>
}

export function AuditLogPanel({
  entityType,
  entityId,
  className = 'mt-5',
}: {
  entityType: string
  entityId: string
  /** Cleared by callers that render this inside a tab, where the margin is the tab's job. */
  className?: string
}) {
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('-occurred')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Any narrowing goes back to page one: staying on page four of a result that now has
  // two is an empty screen that reads as "no history".
  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  const trail = useAsync<AuditTrail>(
    (signal) =>
      api.get<AuditTrail>(
        '/audit',
        {
          entityType,
          entityId,
          search: search || undefined,
          sort,
          skip: (page - 1) * pageSize,
          take: pageSize,
        },
        signal,
      ),
    [entityType, entityId, search, sort, page, pageSize],
  )

  const totalPages = Math.max(1, Math.ceil((trail.data?.totalCount ?? 0) / pageSize))

  return (
    <Card className={className}>
      <CardHeader
        title="History"
        hint="Every change to this record, newest first. History is never edited or deleted."
      />

      {/* A 403 is a permanent answer, so it gets a quiet note rather than a retry - and
          the controls go with it, because there is nothing to search. */}
      {trail.error?.status === 403 ? (
        <p className="px-5 py-4 text-[13px] text-muted">
          You do not have permission to see this record's history.
        </p>
      ) : (
        <>
          <div className="px-5 pt-4">
            <ListToolbar
              sort={sort}
              onSortChange={(next) => refine(() => setSort(next))}
              sortChoices={[
                { value: '-occurred', label: 'Newest first' },
                { value: 'occurred', label: 'Oldest first' },
              ]}
            >
              <div className="w-64">
                <Input
                  placeholder="Search changes, values or who"
                  value={search}
                  onChange={(event) => refine(() => setSearch(event.target.value))}
                />
              </div>
            </ListToolbar>
          </div>


          {trail.error && (
            <div className="px-5 py-4">
              <ErrorState
                title={trail.error.title}
                detail={trail.error.detail}
                onRetry={trail.reload}
              />
            </div>
          )}

          {trail.data && trail.data.entries.length === 0 && (
            <EmptyState
              title={search ? 'Nothing matches' : 'No history yet'}
              hint={
                search
                  ? 'No change to this record mentions that.'
                  : 'Changes to this record will appear here.'
              }
            />
          )}

          {trail.data && trail.data.entries.length > 0 && (
            <ol className="flex flex-col border-t border-line-soft">
              {trail.data.entries.map((entry) => (
                <li key={entry.id} className="border-t border-line-soft px-5 py-3 first:border-t-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <Badge tone={TONE[entry.action] ?? 'neutral'}>{entry.action}</Badge>

                    <span className="text-[13px] font-medium">{entry.actorName}</span>
                    {entry.actorEmail && (
                      <span className="text-[12px] text-muted">{entry.actorEmail}</span>
                    )}

                    {/* A child row says so, otherwise "Created" on a customer's history
                        looks like the customer was created twice. */}
                    {entry.entityType !== entityType && (
                      <span className="font-mono text-[11px] text-faint">
                        {fieldLabel(entry.entityType)}
                      </span>
                    )}

                    <span className="ml-auto text-[12px] text-muted" title={entry.occurredAt}>
                      {shortDate(entry.occurredAt)} · {relative(entry.occurredAt)}
                    </span>
                  </div>

                  {entry.changes.length > 0 && (
                    <dl className="mt-2 grid grid-cols-[minmax(7rem,auto)_1fr] gap-x-4 gap-y-1 text-[12.5px]">
                      {entry.changes.map((change) => (
                        <div key={change.field} className="contents">
                          <dt className="text-muted">{fieldLabel(change.field)}</dt>
                          <dd className="min-w-0">
                            {entry.action === 'Created' ? (
                              <Value value={change.after} />
                            ) : entry.action === 'Deleted' ? (
                              <Value value={change.before} />
                            ) : (
                              <>
                                <Value value={change.before} />
                                <span className="mx-2 text-faint" aria-label="became">
                                  →
                                </span>
                                <Value value={change.after} />
                              </>
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </li>
              ))}
            </ol>
          )}

          <Busy busy={trail.loading}>
            {trail.data && (
              <Pagination
                page={page}
                pageSize={pageSize}
                totalCount={trail.data.totalCount}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={(next) => refine(() => setPageSize(next))}
              />
            )}
          </Busy>
        </>
      )}
    </Card>
  )
}

/**
 * The same history, opened from a row in a listing.
 *
 * A listing row's own dialog owns a Save and a Cancel; history is immutable, so it gets
 * its own read-only surface rather than becoming a tab inside a form that can be edited.
 */
export function AuditHistoryButton({
  entityType,
  entityId,
  title,
}: {
  entityType: string
  entityId: string
  title: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button
        size="sm"
        onClick={(event) => {
          // The row itself is usually clickable; opening history should not also open the
          // record behind it.
          event.stopPropagation()
          setOpen(true)
        }}
      >
        History
      </Button>

      <Dialog
        open={open}
        wide
        title={title}
        description="Every change to this record."
        onClose={() => setOpen(false)}
      >
        {open && <AuditLogPanel entityType={entityType} entityId={entityId} className="" />}
      </Dialog>
    </>
  )
}
